import React, { useEffect, useMemo, useState } from "react";

import "./BusStudentIdCard.css";
import { imgBaseUrl } from "../../../api/api";
import { formatToDate } from "../../master/FormatToDateTime";

const DEFAULT_THEME = "#123c73";

export default function BusStudentIdCard({ data, themeColor }) {
  const [setting, setSetting] = useState({});

  useEffect(() => {
    try {
      setSetting(JSON.parse(localStorage.getItem("setting") ?? "{}"));
    } catch {
      setSetting({});
    }
  }, []);

  const student = data?.student ?? {};
  const bus = data?.bus ?? {};
  const stop = data?.stop ?? {};

  // Any single hex works here — every gradient, badge, and strip below
  // derives its shade from this one value via color-mix(), so nothing
  // clashes no matter what color is chosen.
  const accent =
    themeColor || data?.theme_color || setting?.theme_color || DEFAULT_THEME;

  const instituteInitials = useMemo(() => {
    const name = setting?.name || "Institute";
    return name
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }, [setting?.name]);

  return (
    <div className="bus-id-card" style={{ "--accent": accent }}>
      {/* HEADER */}
      <div className="bus-id-header">
        <div className="bus-id-logo">
          {setting?.logo_img ? (
            <img src={imgBaseUrl + setting.logo_img} alt="Institute Logo" />
          ) : (
            <span className="bus-id-logo-fallback">{instituteInitials}</span>
          )}
        </div>

        <div className="bus-id-institute">
          <div className="bus-id-institute-name">
            {setting?.name || "INSTITUTE NAME"}
          </div>

          <div className="bus-id-tagline">
            {setting?.tag_line || "STUDENT TRANSPORT ID CARD"}
          </div>

          <div className="bus-id-contact">
            {setting?.mobile ? `Mob: ${setting.mobile}` : ""}
          </div>
        </div>
      </div>

      {/* PHOTO */}
      <div className="bus-id-photo-section mt-1">
        <div className="bus-id-photo">
          <img
            src={
              student?.profile_picture
                ? imgBaseUrl + student.profile_picture
                : "/files/user.png"
            }
            alt="Student"
          />
        </div>

        <div className="bus-id-student-title">
          <div className="bus-id-student-name">
            {student?.name || "Student Name"}
          </div>

          <div className="bus-id-registration">
            Reg. No. {student?.registration_no || "-"}
          </div>
        </div>
      </div>

      {/* STUDENT DETAILS */}
      <div className="bus-id-details">
        <div className="bus-id-detail-row">
          <span>Name</span>
          <b>{student?.name || "-"}</b>
        </div>

        <div className="bus-id-detail-row">
          <span>Father's Name</span>
          <b>{student?.father_name || "-"}</b>
        </div>

        <div className="bus-id-detail-row">
          <span>Mobile</span>
          <b>{student?.phone || "-"}</b>
        </div>

        <div className="bus-id-detail-row">
          <span>Bus Stop</span>
          <b>{stop?.stop_name || "-"}</b>
        </div>

        <div className="bus-id-detail-row">
          <span>Joining Date</span>
          <b>{data?.joining_date ? formatToDate(data.joining_date) : "-"}</b>
        </div>

        <div className="bus-id-detail-row">
          <span>Payment</span>
          <b>{data?.payment_type || "-"}</b>
        </div>

        <div className="bus-id-detail-row">
          <span>Address</span>
          <b>
            {[
              student?.village,
              student?.district,
              student?.state,
              student?.zipcode,
            ]
              .filter(Boolean)
              .join(", ") || "-"}
          </b>
        </div>
      </div>

      {/* BUS INFO */}
      <div className="bus-id-bus-info">
        <div className="bus-info-item">
          <span>BUS</span>
          <strong>{bus?.bus_number || "-"}</strong>
        </div>

        <div className="bus-info-divider" />

        <div className="bus-info-item">
          <span>DRIVER</span>
          <strong>{bus?.driver_name || "-"}</strong>
        </div>

        <div className="bus-info-divider" />

        <div className="bus-info-item">
          <span>STOP</span>
          <strong>{stop?.stop_name || "-"}</strong>
        </div>
      </div>

      {/* FOOTER */}
      <div className="bus-id-footer">
        <div className="bus-id-footer-address">
          {[setting?.village, setting?.district, setting?.state]
            .filter(Boolean)
            .join(", ")}
        </div>

        <div className="bus-id-signature">
          {setting?.signature ? (
            <img src={imgBaseUrl + setting.signature} alt="Signature" />
          ) : (
            <span className="bus-id-signature-line" />
          )}
          <span>Authorized Signature</span>
        </div>
      </div>
    </div>
  );
}